import React from 'react'
import { Badge, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

export interface LiveViewBadgeProps {
  iconComponent: React.ReactNode
  badgeAmount?: number
  overlayIcon?: React.ReactNode
}

const useStyles = makeStyles()(theme => {
  return {
    dynamicBadge: {
      '> .MuiBadge-anchorOriginTopRight': {
        backgroundColor: theme.palette.primary.light,
        maxWidth: '20px',
        right: '90%',
      },
    },
  }
})

/**
 * This component displays an icon with an optional badge and overlay icon.
 * If the badge amount is greater than 1, it shows a badge with the count.
 * If an overlay icon is provided, it will be displayed on top of the badge.
 */
const LiveViewBadge: React.FC<LiveViewBadgeProps> = ({ iconComponent, badgeAmount = 0, overlayIcon }) => {
  const { classes } = useStyles()

  const content =
    badgeAmount > 1 ? (
      <Badge
        badgeContent={badgeAmount}
        sx={{
          ...(overlayIcon && { width: '100%', height: '100%', minWidth: 0, minHeight: 0 }),
          '& .MuiBadge-badge': {
            backgroundColor: 'liveView.energyBubble',
            color: 'common.white',
          },
        }}
        component={'div'}
        overlap="circular"
      >
        {iconComponent}
      </Badge>
    ) : (
      iconComponent
    )

  if (!overlayIcon) return content

  if (badgeAmount <= 1) {
    return (
      <Badge badgeContent={overlayIcon} overlap="circular" className={classes.dynamicBadge}>
        {iconComponent}
      </Badge>
    )
  }

  // The overlay icon can currently be displayed only for the wallbox item with two badges.
  // If other items need an overlay icon, additional handling may be required or at least other width and height.
  return (
    <Box
      sx={{
        position: 'relative',
        width: 52,
        height: 59,
      }}
    >
      {content}
      <Badge
        badgeContent={overlayIcon}
        overlap="circular"
        className={classes.dynamicBadge}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      >
        <Box sx={{ width: '100%', height: '100%' }} />
      </Badge>
    </Box>
  )
}

export default LiveViewBadge
